import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { formatPrice } from "../utils/helpers";
import { resolveBackendAssetUrl } from "../utils/urls";
import { createDefaultProductBanners, getContrastTextColor, normalizeProductBanners } from "../utils/productBanners";
import { getConfiguredProductOptions, normalizeSelectedOptions } from "../utils/productOptions";
import { toast } from "react-toastify";
import { motion } from "framer-motion";

export default function ProductDetail() {
  const { id } = useParams();
  const { canSeePrices } = useAuth();
  const { addToCart } = useCart();
  const { exchangeRates, selectedCurrency } = useCurrency();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [productOptionSets, setProductOptionSets] = useState([]);
  const [productBanners, setProductBanners] = useState(createDefaultProductBanners());
  const [selectedOptions, setSelectedOptions] = useState({});

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const [productResponse, settingsResponse, bannerResponse] = await Promise.all([
          api.get(`/products/${id}`),
          api.get("/settings/product-form"),
          api.get("/settings/banners"),
        ]);
        setProduct(productResponse.data.product);
        setProductOptionSets(settingsResponse.data.settings?.product_option_sets || []);
        setProductBanners(normalizeProductBanners(bannerResponse.data.settings?.product_banners));
        setSelectedOptions({});
      } catch (error) {
        console.error("Error fetching product:", error);
        toast.error("Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const configuredProductOptions = useMemo(
    () => getConfiguredProductOptions(productOptionSets, product?.product_option_values),
    [productOptionSets, product?.product_option_values],
  );

  useEffect(() => {
    if (configuredProductOptions.length === 0) {
      return;
    }

    setSelectedOptions((current) => {
      const nextSelectedOptions = {};

      configuredProductOptions.forEach((optionSet) => {
        const currentSelection = current[optionSet.id];
        if (currentSelection?.value && optionSet.options.includes(currentSelection.value)) {
          nextSelectedOptions[optionSet.id] = currentSelection;
        }
      });

      return nextSelectedOptions;
    });
  }, [product?.id, configuredProductOptions]);

  const handleAddToCart = async () => {
    const normalizedSelectedOptions = normalizeSelectedOptions(selectedOptions);

    if (configuredProductOptions.some((optionSet) => !normalizedSelectedOptions[optionSet.id]?.value)) {
      toast.error("Please select all product options before adding to cart");
      return;
    }

    await addToCart(product, quantity, normalizedSelectedOptions);
  };

  const handleSelectedOptionChange = (optionSet, optionValue) => {
    setSelectedOptions((current) => {
      if (!optionValue) {
        const nextSelectedOptions = { ...current };
        delete nextSelectedOptions[optionSet.id];
        return nextSelectedOptions;
      }

      return {
        ...current,
        [optionSet.id]: {
          name: optionSet.name,
          value: optionValue,
        },
      };
    });
  };

  const getProductStatus = () => {
    if (!product) return null;
    if ((product.stock_level ?? 0) <= 0) return { label: "Out of Stock", color: "gray", canOrder: false };
    if (product.status === "discontinued") return { label: "Discontinued", color: "red", canOrder: false };
    if (product.status === "coming_soon") return { label: "Coming Soon", color: "blue", canOrder: false };
    if (product.status === "pre_order") return { label: "Pre-Order Available", color: "purple", canOrder: true };
    return { label: "In Stock", color: "green", canOrder: true };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-gold-500">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">Product not found</p>
          <Link to="/products" className="btn-primary">
            Browse Products
          </Link>
        </div>
      </div>
    );
  }

  const status = getProductStatus();
  const isOutOfStock = (product.stock_level ?? 0) <= 0;
  const shouldShowPurchaseControls = product.status !== "discontinued" && product.status !== "coming_soon";
  const images = product.images || [];
  const primaryImage = images.find((img) => img.is_primary) || images[0];

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <div className="mb-3">
          <Link to="/products" className="text-xs font-semibold uppercase tracking-[0.08em] text-gold-500 transition-colors duration-200 hover:text-gold-300">
            ← Back to Products
          </Link>
        </div>

        {/* Breadcrumb */}
        <div className="mb-8 text-sm text-gray-400">
          <Link to="/" className="hover:text-gold-500">
            Home
          </Link>
          <span className="mx-2">/</span>
          <Link to="/products" className="hover:text-gold-500">
            Products
          </Link>
          <span className="mx-2">/</span>
          <span className="text-white">{product.category_name}</span>
          <span className="mx-2">/</span>
          <span className="text-white">{product.name}</span>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
          {/* Images */}
          <div>
            {/* Main Image */}
            <motion.div key={selectedImage} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-gradient-to-r from-black via-gray-900 to-black rounded-lg mb-4 flex min-h-[24rem] items-center justify-center p-4">
              {images.length > 0 ? (
                <img 
                  src={resolveBackendAssetUrl(images[selectedImage]?.url || primaryImage?.url)} 
                  alt={product.name} 
                  className="max-h-[32rem] w-auto max-w-full object-contain" 
                />
              ) : (
                <div className="flex min-h-[24rem] w-full items-center justify-center text-gray-600">No Image Available</div>
              )}
            </motion.div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 sm:gap-4">
                {images.map((image, index) => (
                  <button key={image.id} onClick={() => setSelectedImage(index)} className={`flex h-24 items-center justify-center rounded-lg border-2 bg-gradient-to-r from-black via-gray-900 to-black p-2 transition-colors sm:h-28 ${selectedImage === index ? "border-gold-500" : "border-transparent"} hover:border-gold-500`}>
                    <img 
                      src={resolveBackendAssetUrl(image.url)} 
                      alt={`${product.name} view ${index + 1}`} 
                      className="max-h-full w-auto max-w-full object-contain" 
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            {/* Badges */}
            <div className="mb-4 flex flex-wrap gap-2">
              {(product.selected_banner_ids || []).map((bannerId) => {
                const banner = productBanners[bannerId];

                if (!banner) {
                  return null;
                }

                return (
                  <span
                    key={bannerId}
                    className="rounded px-3 py-1 text-xs font-bold"
                    style={{
                      backgroundColor: banner.backgroundColor,
                      color: getContrastTextColor(banner.backgroundColor),
                    }}
                  >
                    {banner.name}
                  </span>
                );
              })}
              {status && <span className={`bg-${status.color}-600 text-white px-3 py-1 text-xs font-bold rounded`}>{status.label}</span>}
            </div>

            {/* Title */}
            <h1 className="heading-metallic mb-2 text-3xl sm:text-4xl">{product.name}</h1>

            {/* SKU */}
            <p className="text-gray-500 text-sm mb-4">SKU: {product.sku}</p>

            {/* Price */}
            {canSeePrices() ? (
              <div className="text-3xl font-bold text-gold-500 mb-6">{formatPrice(product.price, selectedCurrency, exchangeRates)}</div>
            ) : (
              <div className="bg-gradient-to-r from-black via-gray-900 to-black border border-gray-700 rounded-lg p-4 mb-6">
                <p className="text-gray-400 mb-2">Prices are only visible to approved trade customers</p>
                <Link to="/register" className="text-gold-500 hover:underline">
                  Apply for Trade Account
                </Link>
              </div>
            )}

            {/* Description */}
            <div className="prose prose-invert mb-6">
              <p className="text-gray-300 leading-relaxed">{product.description}</p>
            </div>

            {/* Product Options */}
            <div className="bg-gradient-to-r from-black via-gray-900 to-black rounded-lg p-6 mb-6">
              <h3 className="heading-metallic-soft text-lg mb-4">Product Options</h3>

              <div className="divide-y divide-gray-800">
                {configuredProductOptions.length > 0 ? configuredProductOptions.map((optionSet) => (
                  <div key={optionSet.id} className="grid gap-3 py-3 md:grid-cols-[minmax(0,11rem)_1fr] md:items-center md:gap-6">
                    <label className="text-sm font-medium text-gray-300 md:mb-0">{optionSet.name}</label>
                    <select
                      value={selectedOptions[optionSet.id]?.value || ""}
                      onChange={(e) => handleSelectedOptionChange(optionSet, e.target.value)}
                      className="w-full rounded border border-gray-700 bg-black px-3 py-2 text-sm text-white focus:border-gold focus:outline-none"
                    >
                      <option value="">Select {optionSet.name}</option>
                      {optionSet.options.map((optionValue) => (
                        <option key={optionValue} value={optionValue}>
                          {optionValue}
                        </option>
                      ))}
                    </select>
                  </div>
                )) : (
                  <div className="py-3 text-sm text-gray-500">No selectable product options for this item.</div>
                )}

              </div>
            </div>

            {/* Add to Cart */}
            {canSeePrices() && shouldShowPurchaseControls && (
              <div className="space-y-4">
                {/* Quantity Selector */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <label className="text-gray-400">Quantity:</label>
                  <div className={`flex items-center border rounded ${isOutOfStock ? "border-gray-800 opacity-60" : "border-gray-700"}`}>
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={isOutOfStock} className="px-4 py-2 hover:bg-gray-800 disabled:cursor-not-allowed disabled:hover:bg-transparent">
                      -
                    </button>
                    <input type="number" value={quantity} disabled={isOutOfStock} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} className={`w-16 text-center bg-transparent border-x py-2 ${isOutOfStock ? "border-gray-800 text-gray-500" : "border-gray-700"}`} />
                    <button onClick={() => setQuantity(quantity + 1)} disabled={isOutOfStock} className="px-4 py-2 hover:bg-gray-800 disabled:cursor-not-allowed disabled:hover:bg-transparent">
                      +
                    </button>
                  </div>
                </div>

                <button onClick={handleAddToCart} disabled={isOutOfStock} className={`w-full text-lg py-4 ${isOutOfStock ? "bg-gray-800 text-gray-400 border border-gray-700 rounded-lg cursor-not-allowed" : "btn-primary"}`}>
                  {isOutOfStock ? "Out of Stock" : product.status === "pre_order" ? "Pre-Order Now" : "Add to Cart"}
                </button>

                {product.status === "pre_order" && product.release_date && <p className="text-sm text-gray-400 text-center">Expected release: {new Date(product.release_date).toLocaleDateString()}</p>}
              </div>
            )}

            {!canSeePrices() && (
              <div className="text-center py-6">
                <Link to="/login" className="btn-primary inline-block">
                  Login to Order
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
